import compact from 'lodash/compact';
import { ReactNode, useCallback } from 'react';

import { VisitPrints, VisitPrintsQuery } from '@dreryk/egabinet-visit-frontend-api';

import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { printDocumentAsPdf } from '@dreryk/react-common';
import { ActionEventInterceptor } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { useMedicalDocumentSigningStatusHandler } from '~libs.desktop.shared.medical-documentation';

import { Prints } from './form/PrintsForm.types';
import { mapOtherPrintDocuments, mapPrintableToDocumentPdf } from './PrintVisitDocumentsActionEventInterceptor.utils';
import { translationKeys } from './translations';

type PrintVisitDocumentsActionEventInterceptorProps = {
   readonly children: ReactNode;
   readonly prints: Prints;
   readonly visitId: number;
};

export const PrintVisitDocumentsActionEventInterceptor = (
   props: PrintVisitDocumentsActionEventInterceptorProps
): JSX.Element => {
   const creatingMedicalDocumentsAvailable = protectFeature('creatingMedicalDocuments');
   const handleMedicalDocumentSigningStatus = useMedicalDocumentSigningStatusHandler();

   const handleAction = useCallback(async () => {
      const visitPrints = await performCqrsQueryGet<VisitPrints>(() => {
         const query = new VisitPrintsQuery();
         query.prints = {
            recommendations: props.prints.recommendations,
            physicalExamination: props.prints.subjectiveAssessment,
            otherDocuments: props.prints.otherDocuments,
            summary: props.prints.summary,
         };
         query.visitId = props.visitId;
         return query;
      });

      const summaryPrint = mapPrintableToDocumentPdf(visitPrints.summary);
      const physicalExaminationPrint = mapPrintableToDocumentPdf(visitPrints.physicalExamination);
      const recommendationsPrint = mapPrintableToDocumentPdf(visitPrints.recommendations);
      const otherPrintDocuments = mapOtherPrintDocuments(visitPrints.otherDocuments);

      await printDocumentAsPdf(
         ...compact([summaryPrint, physicalExaminationPrint, recommendationsPrint, ...otherPrintDocuments])
      );

      if (creatingMedicalDocumentsAvailable) {
         [
            visitPrints.summarySigningStatus,
            visitPrints.recommendationsSigningStatus,
            visitPrints.physicalExaminationSigningStatus,
         ].forEach(handleMedicalDocumentSigningStatus);
      }
   }, [creatingMedicalDocumentsAvailable, handleMedicalDocumentSigningStatus, props.prints, props.visitId]);

   return (
      <ActionEventInterceptor action={handleAction} blockingOperation={translationKeys.printingDocuments}>
         {props.children}
      </ActionEventInterceptor>
   );
};
